import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface LiveStatusPanelProps {
  /** Extra classes for the outer wrapper */
  className?: string;
}

interface LiveValues {
  threats: number;
  blocked: number;
  response: number;
  uptime: number;
}

interface Metric {
  icon: string;
  label: string;
  value: string;
  pct: number;
  color: string;
  bar: string;
}

export default function LiveStatusPanel({ className = "" }: LiveStatusPanelProps) {
  const { t } = useTranslation("common");
  const [ready, setReady] = useState(false);
  const [values, setValues] = useState<LiveValues>({
    threats: 28473,
    blocked: 23158,
    response: 4,
    uptime: 99.97,
  });

  useEffect(() => {
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Simulate a live feed: counters keep ticking as new events are collected
  useEffect(() => {
    const id = setInterval(() => {
      setValues((prev) => ({
        threats: prev.threats + Math.floor(Math.random() * 34) + 4,
        blocked: prev.blocked + Math.floor(Math.random() * 30) + 3,
        response: Math.min(6, Math.max(3, prev.response + (Math.random() < 0.5 ? -1 : 1))),
        uptime: Number((99.94 + Math.random() * 0.05).toFixed(2)),
      }));
    }, 2200);
    return () => clearInterval(id);
  }, []);

  const metrics: Metric[] = [
    {
      icon: "ri-radar-line",
      label: t("cs.hero.panel.threats"),
      value: values.threats.toLocaleString("en-US"),
      pct: 72 + (values.threats % 14),
      color: "text-primary-300",
      bar: "from-primary-500 to-primary-300",
    },
    {
      icon: "ri-shield-check-line",
      label: t("cs.hero.panel.blocked"),
      value: values.blocked.toLocaleString("en-US"),
      pct: 86 + (values.blocked % 10),
      color: "text-emerald-300",
      bar: "from-emerald-500 to-emerald-300",
    },
    {
      icon: "ri-time-line",
      label: t("cs.hero.panel.response"),
      value: `${values.response} min`,
      pct: 74 + (6 - values.response) * 5,
      color: "text-cyan-300",
      bar: "from-cyan-500 to-cyan-300",
    },
    {
      icon: "ri-server-line",
      label: t("cs.hero.panel.uptime"),
      value: `${values.uptime.toFixed(2)}%`,
      pct: 96 + Math.round((values.uptime - 99.94) * 40),
      color: "text-accent-300",
      bar: "from-accent-500 to-accent-300",
    },
  ];

  return (
    <div className={`relative ${className}`}>
      <div className="absolute -inset-2 bg-primary-500/10 rounded-[1.5rem] blur-2xl pointer-events-none" />
      <div className="relative rounded-2xl border border-white/12 bg-background-950/55 backdrop-blur-xl p-4 md:p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[10px] font-label font-semibold text-foreground-100/80 uppercase tracking-[0.15em]">
              {t("cs.hero.panel.title")}
            </span>
          </div>
          <span className="text-[9px] font-label text-emerald-300/70 uppercase tracking-widest">LIVE</span>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          {metrics.map((m) => (
            <div key={m.label}>
              <div className="flex items-center gap-1.5 mb-1">
                <i className={`${m.icon} ${m.color} text-xs`} />
                <span className="text-[9px] font-label text-foreground-200/60 uppercase tracking-wider leading-tight">
                  {m.label}
                </span>
              </div>
              <p
                key={m.value}
                className={`font-heading font-bold text-lg leading-none mb-1.5 animate-value-tick ${m.color}`}
              >
                {m.value}
              </p>
              <div className="h-1 rounded-full bg-white/8 overflow-hidden">
                <div
                  className={`h-full rounded-full bg-gradient-to-r ${m.bar} transition-[width] duration-1000 ease-out`}
                  style={{ width: ready ? `${Math.min(100, m.pct)}%` : "0%" }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}